import { useState, useEffect, useCallback, useMemo } from 'react';
import { request } from '../../../utils/api';
import { toast } from 'react-hot-toast';
import { useParams, useSearchParams } from 'react-router-dom';
import { USER_ROLES, getDocumentTabsForRole, isDocumentTabVisibleForRole, resolveUserRoleName } from '../../../constants';
import { toIsoDate } from '../../../utils/dateUtils';
import { getSessionTenantId, getSessionUserId } from '../../../utils/tenantContext';

export const useDocuments = () => {
    const { id } = useParams(); // Start with URL param if viewing another employee
    const [searchParams, setSearchParams] = useSearchParams();
    const initialTab = searchParams.get('tab') || 'application-forms';
    const [activeTab, _setActiveTab] = useState(initialTab);

    const setActiveTab = useCallback((tab) => {
        _setActiveTab(tab);
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set("tab", tab);
            return next;
        }, { replace: true });
    }, [setSearchParams]);

    useEffect(() => {
        const fromUrl = searchParams.get("tab");
        if (fromUrl && fromUrl !== activeTab) {
            _setActiveTab(fromUrl);
        }
    }, [searchParams, activeTab]);

    const [viewMode, setViewMode] = useState('documents'); // 'user-list' or 'documents'

    // User Context
    const [currentUser, setCurrentUser] = useState(null);
    const [isAdmin, setIsAdmin] = useState(false);

    // User List State (for Admin)
    const [usersList, setUsersList] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [userPage, setUserPage] = useState(1);
    const [totalUserPages, setTotalUserPages] = useState(1);
    const [totalUsers, setTotalUsers] = useState(0);
    const [userLimit, setUserLimit] = useState(20);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    const [userListSort, setUserListSort] = useState({
        sortKey: "status",
        sortOrder: "asc",
    });

    const toggleUserSort = useCallback((key) => {
        setUserListSort((prev) => ({
            sortKey: key,
            sortOrder:
                prev.sortKey === key && prev.sortOrder === "asc" ? "desc" : "asc",
        }));
    }, []);

    const displayedUsersList = useMemo(() => {
        const { sortKey, sortOrder } = userListSort;
        const result = [...usersList];
        if (!sortKey) return result;

        const getSortValue = (u, key) => {
            if (key === "firstName") {
                return `${u.firstName || ""} ${u.lastName || ""}`.trim();
            }
            if (key === "roleName") {
                return u.role?.name ?? "";
            }
            let val = u[key];
            if (val === null || val === undefined) val = "";
            return val;
        };

        return result.sort((a, b) => {
            let valA = getSortValue(a, sortKey);
            let valB = getSortValue(b, sortKey);

            if (valA === null || valA === undefined) valA = "";
            if (valB === null || valB === undefined) valB = "";

            if (typeof valA === "number" && typeof valB === "number") {
                return sortOrder === "asc" ? valA - valB : valB - valA;
            }

            const isDate = (val) =>
                typeof val === "string" && /^\d{4}-\d{2}-\d{2}/.test(val);
            if (isDate(valA) && isDate(valB)) {
                const dateA = new Date(valA).getTime();
                const dateB = new Date(valB).getTime();
                return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
            }

            const strA = valA.toString().toLowerCase();
            const strB = valB.toString().toLowerCase();

            if (strA < strB) return sortOrder === "asc" ? -1 : 1;
            if (strA > strB) return sortOrder === "asc" ? 1 : -1;
            return 0;
        });
    }, [usersList, userListSort]);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 500);

        return () => clearTimeout(handler);
    }, [searchTerm]);

    // Reset to page 1 when search changes
    useEffect(() => {
        setUserPage(1);
    }, [debouncedSearch]);

    // Application Forms State
    const [applicationForms, setApplicationForms] = useState([]);
    const [loadingAppForms, setLoadingAppForms] = useState(false);
    const [appFormsPage, setAppFormsPage] = useState(1);
    const [totalAppFormsPages, setTotalAppFormsPages] = useState(1);

    // Credentials State
    const [credentials, setCredentials] = useState([]);
    const [loadingCredentials, setLoadingCredentials] = useState(false);
    const [credentialsPage, setCredentialsPage] = useState(1);
    const [totalCredentialsPages, setTotalCredentialsPages] = useState(1);

    // Medical Forms State
    const [medicalForms, setMedicalForms] = useState([]);
    const [loadingMedicalForms, setLoadingMedicalForms] = useState(false);
    const [medicalFormsPage, setMedicalFormsPage] = useState(1);
    const [totalMedicalFormsPages, setTotalMedicalFormsPages] = useState(1);

    // Tax Forms State
    const [taxForms, setTaxForms] = useState([]);
    const [loadingTaxForms, setLoadingTaxForms] = useState(false);
    const [taxFormsPage, setTaxFormsPage] = useState(1);
    const [totalTaxFormsPages, setTotalTaxFormsPages] = useState(1);

    // Evaluation Forms State
    const [evaluationForms, setEvaluationForms] = useState([]);
    const [loadingEvaluationForms, setLoadingEvaluationForms] = useState(false);
    const [evaluationFormsPage, setEvaluationFormsPage] = useState(1);
    const [totalEvaluationFormsPages, setTotalEvaluationFormsPages] = useState(1);

    // Totals and Limits for serial numbers
    const [appFormsTotal, setAppFormsTotal] = useState(0);
    const [appFormsLimit, setAppFormsLimit] = useState(10);
    const [credentialsTotal, setCredentialsTotal] = useState(0);
    const [credentialsLimit, setCredentialsLimit] = useState(10);
    const [medicalFormsTotal, setMedicalFormsTotal] = useState(0);
    const [medicalFormsLimit, setMedicalFormsLimit] = useState(10);
    const [taxFormsTotal, setTaxFormsTotal] = useState(0);
    const [taxFormsLimit, setTaxFormsLimit] = useState(10);
    const [evaluationFormsTotal, setEvaluationFormsTotal] = useState(0);
    const [evaluationFormsLimit, setEvaluationFormsLimit] = useState(10);

    // Selected User State (for Admin viewing specific employee)
    const [selectedUser, setSelectedUser] = useState(null);

    const documentRoleName = useMemo(() => {
        if (id) {
            if (!selectedUser) return null;
            return resolveUserRoleName(selectedUser);
        }
        return resolveUserRoleName(selectedUser || currentUser);
    }, [id, selectedUser, currentUser]);

    const visibleDocumentTabs = useMemo(() => {
        if (documentRoleName === null) {
            return ["application-forms"];
        }
        return getDocumentTabsForRole(documentRoleName);
    }, [documentRoleName]);

    useEffect(() => {
        if (viewMode !== 'documents') return;
        if (documentRoleName === null) return;
        if (!isDocumentTabVisibleForRole(activeTab, documentRoleName)) {
            setActiveTab('application-forms');
        }
    }, [viewMode, activeTab, documentRoleName, setActiveTab]);

    useEffect(() => {
        const userStr = localStorage.getItem("currentUser");
        if (userStr) {
            const user = JSON.parse(userStr);
            setCurrentUser(user);
            const isSurveyor =
                user.role?.name === USER_ROLES.SRVY || user.roleId === 12;
            const isUserAdmin = user.roleId === 1;
            setIsAdmin(isUserAdmin);

            // Admin or Surveyor: pick an employee first when no :id in the URL
            if ((isUserAdmin || isSurveyor) && !id) {
                setViewMode('user-list');
                // fetchUsers(page); // Fetched via useEffect dependency on userPage
            } else {
                setViewMode('documents');
                if (id) {
                    fetchUserDetails(id);
                } else {
                    // If viewing own documents, set selectedUser as current user
                    setSelectedUser(user);
                }
            }
        }
    }, [id]);

    const fetchUserDetails = async (userId) => {
        try {
            const response = await request(`/users/${userId}`);
            if (response.status === 1) {
                setSelectedUser(response.result.user);
            }
        } catch (error) {
            console.error("Error fetching user details:", error);
        }
    };

    const fileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result.split(',')[1]);
            reader.onerror = (error) => reject(error);
        });
    };

    const saveCredential = async (data, mode, credentialId) => {
        const { tenantId, employeeId } = getContext();
        if (!employeeId) {
            toast.error("Employee context not found");
            return false;
        }

        try {
            let payload = {
                tenantId,
                employeeId: parseInt(employeeId),
                type: data.type || "professional_license", // Fallback or dynamic
                displayName: data.displayName,
                notes: data.notes,
                expireAt: toIsoDate(data.expireAt)
            };

            // Handle File: Prioritize fileBase64 from Zod form
            if (data.fileBase64 && data.fileBase64 !== 'EXISTING_FILE_PLACEHOLDER') {
                // If it's a raw base64 string (from FileReader)
                // It usually comes as "data:application/pdf;base64,JVBER..."
                // We typically need to strip the prefix if the backend expects raw base64.
                // However, the previous implementation used `reader.result.split(',')[1]`.
                // So we should do the same here if the prefix exists.
                const base64Data = data.fileBase64.includes(',')
                    ? data.fileBase64.split(',')[1]
                    : data.fileBase64;

                payload.fileBase64 = base64Data;
                payload.fileName = data.fileName;
            } else if (data.file) {
                // Fallback for legacy calls (if any)
                payload.fileBase64 = await fileToBase64(data.file);
                payload.fileName = data.fileName;
            } else if (!data.fileBase64 && mode === 'edit') {
                // Explicitly send null to clear the file if it was removed
                payload.fileBase64 = null;
                payload.fileName = null;
            }

            let response;
            if (mode === 'create') {
                response = await request('/credentials', {
                    method: 'POST',
                    body: JSON.stringify(payload)
                });
            } else {
                // For edit, requirement postman PUT '/api/credentials/1'
                response = await request(`/credentials/${credentialId}`, {
                    method: 'PUT',
                    body: JSON.stringify(payload)
                });
            }

            if (response.status === 1) {
                toast.success(response.message || "Credential saved successfully");
                fetchCredentials();
                return true;
            } else {
                toast.error(response.message || "Failed to save credential");
                return false;
            }
        } catch (error) {
            console.error("Error saving credential:", error);
            toast.error("An error occurred while saving");
            return false;
        }
    };

    const deleteCredential = async (credentialId) => {
        try {
            const response = await request(`/credentials/${credentialId}`, {
                method: 'DELETE'
            });

            if (response.status === 1) {
                toast.success(response.message || "Credential deleted successfully");
                fetchCredentials();
                return true;
            } else {
                toast.error(response.message || "Failed to delete credential");
                return false;
            }
        } catch (error) {
            console.error("Error deleting credential:", error);
            toast.error("An error occurred while deleting");
            return false;
        }
    };

    const saveTaxForm = async (data, mode, taxFormId) => {
        const { tenantId, employeeId } = getContext();
        if (!employeeId) {
            toast.error("Employee context not found");
            return false;
        }

        try {
            let payload = {
                tenantId,
                userId: parseInt(employeeId),
                type: data.type,
                comments: data.notes || ''
            };

            if (data.fileBase64 && data.fileBase64 !== 'EXISTING_FILE_PLACEHOLDER') {
                const base64Data = data.fileBase64.includes(',')
                    ? data.fileBase64.split(',')[1]
                    : data.fileBase64;

                payload.fileBase64 = base64Data;
                payload.fileName = data.fileName;
            }
            // When editing without a new file, omit file fields so the existing document is kept.
            let response;
            if (mode === 'create') {
                response = await request('/tax-and-payroll', {
                    method: 'POST',
                    body: JSON.stringify(payload)
                });
            } else {
                response = await request(`/tax-and-payroll/${taxFormId}`, {
                    method: 'PUT',
                    body: JSON.stringify(payload)
                });
            }

            if (response.status === 1) {
                toast.success(response.message || "Tax form saved successfully");
                fetchTaxForms();
                return true;
            } else {
                toast.error(response.message || "Failed to save tax form");
                return false;
            }
        } catch (error) {
            console.error("Error saving tax form:", error);
            toast.error(error?.message || "An error occurred while saving");
            return false;
        }
    };

    const deleteTaxForm = async (taxFormId) => {
        try {
            const response = await request(`/tax-and-payroll/${taxFormId}`, {
                method: 'DELETE'
            });

            if (response.status === 1) {
                toast.success(response.message || "Tax form deleted successfully");
                fetchTaxForms();
                return true;
            } else {
                toast.error(response.message || "Failed to delete tax form");
                return false;
            }
        } catch (error) {
            console.error("Error deleting tax form:", error);
            toast.error("An error occurred while deleting");
            return false;
        }
    };

    const getCredentialFileUrl = async (credentialId) => {
        try {
            const response = await request(`/credentials/${credentialId}/file-url`);
            return response;
        } catch (error) {
            console.error("Error fetching credential file URL:", error);
            toast.error("Failed to generate view link");
            return null;
        }
    };

    const getTaxFileUrl = async (taxFormId) => {
        try {
            const response = await request(`/tax-and-payroll/${taxFormId}/file-url`);
            return response;
        } catch (error) {
            console.error("Error fetching tax file URL:", error);
            toast.error("Failed to generate view link");
            return null;
        }
    };

    const getCredential = async (credentialId) => {
        try {
            const response = await request(`/credentials/${credentialId}`);
            return response;
        } catch (error) {
            console.error("Error fetching credential:", error);
            // toast.error("Failed to fetch credential details");
            return null;
        }
    };

    const getTaxFormById = async (taxFormId) => {
        try {
            const response = await request(`/tax-and-payroll/${taxFormId}`);
            return response;
        } catch (error) {
            console.error("Error fetching tax form:", error);
            // toast.error("Failed to fetch tax form details");
            return null;
        }
    };

    const deleteEvaluation = async (evaluationId, type) => {
        try {
            const getDeleteEndpoint = (t) => {
                if (!t) return "evaluation-rn";
                const upperT = t.toUpperCase();
                if (upperT === "RN") return "evaluation-rn";
                if (upperT === "PT" || upperT === "PTA") return "evaluation-pt-pta";
                if (upperT === "OT" || upperT === "OTA") return "evaluation-ot-ota";
                if (upperT === "HHA") return "evaluation-hha";
                if (upperT === "LVN") return "evaluation-lvn";
                if (upperT === "MSW") return "evaluation-msw";
                if (upperT === "ST") return "evaluation-st";
                if (upperT === "OA") return "evaluation-oa";
                return "evaluation-rn"; // Default
            };
            const endpoint = getDeleteEndpoint(type);
            const response = await request(`/${endpoint}/${evaluationId}`, {
                method: 'DELETE'
            });

            if (response.status === 1) {
                toast.success(response.message || "Evaluation deleted successfully");
                fetchEvaluationForms(evaluationFormsPage);
                return true;
            } else {
                toast.error(response.message || "Failed to delete evaluation");
                return false;
            }
        } catch (error) {
            console.error("Error deleting evaluation:", error);
            toast.error("An error occurred while deleting");
            return false;
        }
    };

    const deleteDocument = async (formDetailId, formKey, activeTab) => {
        const DELETE_ENDPOINTS = {
            employment_application: "employment-applications",
            contractor: "contractors",
            contractor_agreement: "contractors",
            contractor_form: "contractors",
            reference_background_check: "reference-background-consents",
            welcome_letter: "welcome-letters",
            employment_agreement: "employment-agreements",
            confidentiality_agreement: "confidentiality-agreements",
            state_mandate_compliance: "state-mandate-compliances",
            job_description: "job-descriptions",
            skill_inventory: "skill-inventories",
            post_test: "post-tests",
            roles_and_responsibilities: "roles-and-responsibilities",
            chha_competency_test: "chha-competency-tests",
            administrative_orientation: "administrative-orientations",
            handbook_form: "handbooks",
            identification_card: "identification-cards",
            hepatitis_form: "hepatitis-b-policy",
            orientation_checklist: "orientation-checklists",
            competency: "competencies",
            tb_test_form: "tb-tests",
            flu_vaccination_program: "flu-vaccinations",
            employee_health_examination: "employee-health-examinations"
        };
        const endpoint = DELETE_ENDPOINTS[formKey];
        if (!endpoint) {
            toast.error("Delete is not supported for this form type: " + formKey);
            return false;
        }

        try {
            const response = await request(`/${endpoint}/${formDetailId}`, {
                method: 'DELETE'
            });

            if (response.status === 1) {
                toast.success("Document deleted successfully");
                if (activeTab === 'medical-forms') {
                    fetchMedicalForms();
                } else {
                    fetchApplicationForms();
                }
                return true;
            } else {
                toast.error(response.message || "Failed to delete document");
                return false;
            }
        } catch (error) {
            console.error("Error deleting document:", error);
            toast.error("An error occurred while deleting");
            return false;
        }
    };

    const fetchUsers = useCallback(async (pageVal) => {
        const page = typeof pageVal === 'number' ? pageVal : userPage;
        setLoadingUsers(true);
        try {
            const isSurveyor = currentUser?.role?.name === USER_ROLES.SRVY;
            const tenantId = currentUser?.tenantId;
            let url = isSurveyor
                ? `/surveyor/my-employees?page=${page}&limit=${userLimit}&tenantId=${tenantId}`
                : `/users/roles?role=RN,LVN,PT,PTA,OT,OTA,ST,HHA,MSW,CG,CNA,OA,CON&page=${page}&limit=${userLimit}`;

            if (debouncedSearch) {
                url += `&search=${encodeURIComponent(debouncedSearch)}`;
            }

            const response = await request(url);

            let users = [];
            let totalPages = 1;
            let success = false;

            // Handle standard wrapped response
            if (response.status === 1 && response.result) {
                users = response.result.users || [];
                totalPages = response.result.totalPages || 1;
                success = true;
            }
            // Handle flat response (as reported by user)
            else if (response.users) {
                users = response.users || [];
                totalPages = response.totalPages || 1;
                success = true;
            }

            if (success) {
                setUsersList(users || []);
                setTotalUserPages(totalPages || 1);
                setTotalUsers(response.result?.total || response.total || (users?.length || 0));
                setUserLimit(response.result?.limit || response.limit || 10);
            } else {
                toast.error(response.message || "Failed to fetch users");
            }
        } catch (error) {
            console.error("Error fetching users:", error);
            toast.error("Error loading user list");
        } finally {
            setLoadingUsers(false);
        }
    }, [userPage, currentUser, debouncedSearch, userLimit]);

    useEffect(() => {
        if (viewMode === 'user-list') {
            fetchUsers(userPage);
        }
    }, [userPage, viewMode, fetchUsers, debouncedSearch]);

    // Helper to get current context (tenantId, employeeId)
    const getContext = useCallback(() => {
        const tenantId = getSessionTenantId();
        let employeeId = null;

        if (currentUser) {
            if (id) {
                employeeId = id;
            } else if (currentUser.employeeId) {
                employeeId = currentUser.employeeId;
            } else if (currentUser.id) {
                employeeId = currentUser.id;
            }
        }
        if (!employeeId) {
            employeeId = getSessionUserId();
        }
        return { tenantId, employeeId };
    }, [id, currentUser]);

    const fetchApplicationForms = useCallback(async (pageVal) => {
        const { tenantId, employeeId } = getContext();
        if (!employeeId || !tenantId) return;

        const page = typeof pageVal === 'number' ? pageVal : appFormsPage;
        setLoadingAppForms(true);
        try {
            const response = await request(`/application-forms-list?tenantId=${tenantId}&employeeId=${employeeId}&page=${page}&limit=10`);
            if (response.status === 1) {
                const totalPages = Math.max(1, response.result.totalPages ?? 1);
                if (page > totalPages) {
                    setAppFormsPage(totalPages);
                    await fetchApplicationForms(totalPages);
                    return;
                }
                if (typeof response.result.page === "number" && response.result.page !== page) {
                    setAppFormsPage(response.result.page);
                }
                setApplicationForms(response.result.forms || []);
                setTotalAppFormsPages(response.result.totalPages || 1);
                setAppFormsTotal(response.result.total || 0);
                setAppFormsLimit(response.result.limit || 10);
            } else {
                toast.error(response.message || "Failed to fetch application forms");
            }
        } catch (error) {
            console.error("Error fetching application forms:", error);
        } finally {
            setLoadingAppForms(false);
        }
    }, [getContext, appFormsPage]);

    const fetchCredentials = useCallback(async (pageVal) => {
        const { tenantId, employeeId } = getContext();
        if (!employeeId || !tenantId) return;

        const page = typeof pageVal === 'number' ? pageVal : credentialsPage;
        setLoadingCredentials(true);
        try {
            const response = await request(`/credentials-for-upload-list?tenantId=${tenantId}&employeeId=${employeeId}&page=${page}&limit=10`);
            if (response.status === 1) {
                setCredentials(response.result.forms || []);
                setTotalCredentialsPages(response.result.totalPages || 1);
                setCredentialsTotal(response.result.total || 0);
                setCredentialsLimit(response.result.limit || 10);
            } else {
                toast.error(response.message || "Failed to fetch credentials");
            }
        } catch (error) {
            console.error("Error fetching credentials:", error);
        } finally {
            setLoadingCredentials(false);
        }
    }, [getContext, credentialsPage]);

    const fetchMedicalForms = useCallback(async (pageVal) => {
        const { tenantId, employeeId } = getContext();
        if (!employeeId || !tenantId) return;

        const page = typeof pageVal === 'number' ? pageVal : medicalFormsPage;
        setLoadingMedicalForms(true);
        try {
            const response = await request(`/medical-forms-list?tenantId=${tenantId}&employeeId=${employeeId}&page=${page}&limit=10`);
            if (response.status === 1) {
                const totalPages = Math.max(1, response.result.totalPages ?? 1);
                if (page > totalPages) {
                    setMedicalFormsPage(totalPages);
                    await fetchMedicalForms(totalPages);
                    return;
                }
                if (typeof response.result.page === "number" && response.result.page !== page) {
                    setMedicalFormsPage(response.result.page);
                }
                setMedicalForms(response.result.forms || []);
                setTotalMedicalFormsPages(response.result.totalPages || 1);
                setMedicalFormsTotal(response.result.total || 0);
                setMedicalFormsLimit(response.result.limit || 10);
            } else {
                toast.error(response.message || "Failed to fetch medical forms");
            }
        } catch (error) {
            console.error("Error fetching medical forms:", error);
        } finally {
            setLoadingMedicalForms(false);
        }
    }, [getContext, medicalFormsPage]);

    const fetchTaxForms = useCallback(async (pageVal) => {
        const { tenantId, employeeId } = getContext();
        if (!employeeId || !tenantId) return;

        const page = typeof pageVal === 'number' ? pageVal : taxFormsPage;
        setLoadingTaxForms(true);
        try {
            const response = await request(`/tax-and-payroll-list?tenantId=${tenantId}&employeeId=${employeeId}&page=${page}&limit=10`);
            if (response.status === 1) {
                setTaxForms(response.result.forms || []);
                setTotalTaxFormsPages(response.result.totalPages || 1);
                setTaxFormsTotal(response.result.total || 0);
                setTaxFormsLimit(response.result.limit || 10);
            } else {
                toast.error(response.message || "Failed to fetch tax forms");
            }
        } catch (error) {
            console.error("Error fetching tax forms:", error);
        } finally {
            setLoadingTaxForms(false);
        }
    }, [getContext, taxFormsPage]);

    const fetchEvaluationForms = useCallback(async (pageVal) => {
        const { tenantId, employeeId } = getContext();
        if (!employeeId || !tenantId) return;

        const page = typeof pageVal === 'number' ? pageVal : evaluationFormsPage;
        setLoadingEvaluationForms(true);
        try {
            const response = await request(`/evaluation-list?tenantId=${tenantId}&employeeId=${employeeId}&page=${page}&limit=10`);
            if (response.status === 1) {
                setEvaluationForms(response.result.forms || []);
                setTotalEvaluationFormsPages(response.result.totalPages || 1);
                setEvaluationFormsTotal(response.result.total || 0);
                setEvaluationFormsLimit(response.result.limit || 10);
            } else {
                toast.error(response.message || "Failed to fetch evaluation forms");
            }
        } catch (error) {
            console.error("Error fetching evaluation forms:", error);
        } finally {
            setLoadingEvaluationForms(false);
        }
    }, [getContext, evaluationFormsPage]);

    // Effect to fetch data when tab changes or viewing documents
    useEffect(() => {
        if (viewMode === 'documents') {
            if (activeTab === 'application-forms') fetchApplicationForms(appFormsPage);
            if (activeTab === 'credentials') fetchCredentials(credentialsPage);
            if (activeTab === 'medical-forms') fetchMedicalForms(medicalFormsPage);
            if (activeTab === 'tax-forms') fetchTaxForms(taxFormsPage);
            if (
                activeTab === 'evaluation-forms' &&
                documentRoleName !== null &&
                isDocumentTabVisibleForRole('evaluation-forms', documentRoleName)
            ) {
                fetchEvaluationForms(evaluationFormsPage);
            }
        }
    }, [activeTab, viewMode, appFormsPage, credentialsPage, medicalFormsPage, taxFormsPage, evaluationFormsPage, documentRoleName, fetchApplicationForms, fetchCredentials, fetchMedicalForms, fetchTaxForms, fetchEvaluationForms]);

    return {
        viewMode,
        isAdmin,
        activeTab,
        setActiveTab,
        selectedUser,
        visibleDocumentTabs,
        documentRoleName,
        // User List Data
        usersList,
        displayedUsersList,
        userSortKey: userListSort.sortKey,
        userSortOrder: userListSort.sortOrder,
        toggleUserSort,
        loadingUsers,
        userPage,
        setUserPage, // Expose actual state setter
        totalUserPages,

        searchTerm,
        setSearchTerm,
        // Document Data
        applicationForms,
        loadingAppForms,
        appFormsPage,
        setAppFormsPage,
        totalAppFormsPages,

        credentials,
        loadingCredentials,
        credentialsPage,
        setCredentialsPage,
        totalCredentialsPages,

        medicalForms,
        loadingMedicalForms,
        medicalFormsPage,
        setMedicalFormsPage,
        totalMedicalFormsPages,

        taxForms,
        loadingTaxForms,
        taxFormsPage,
        setTaxFormsPage,
        totalTaxFormsPages,

        evaluationForms,
        loadingEvaluationForms,
        evaluationFormsPage,
        setEvaluationFormsPage,
        totalEvaluationFormsPages,
        // New pagination info
        totalUsers,
        userLimit,
        appFormsTotal,
        appFormsLimit,
        credentialsTotal,
        credentialsLimit,
        medicalFormsTotal,
        medicalFormsLimit,
        taxFormsTotal,
        taxFormsLimit,
        evaluationFormsTotal,
        evaluationFormsLimit,

        saveCredential,
        deleteCredential,
        saveTaxForm,
        deleteTaxForm,
        deleteEvaluation,
        deleteDocument,
        getCredentialFileUrl,
        getTaxFileUrl,
        getCredential,
        getTaxFormById,
        refetchCurrentTab: () => {
            if (activeTab === 'application-forms') fetchApplicationForms(appFormsPage);
            if (activeTab === 'credentials') fetchCredentials(credentialsPage);
            if (activeTab === 'medical-forms') fetchMedicalForms(medicalFormsPage);
            if (activeTab === 'tax-forms') fetchTaxForms(taxFormsPage);
            if (activeTab === 'evaluation-forms') fetchEvaluationForms(evaluationFormsPage);
        }
    };
};
